import { useRef, useState } from "react";
import type { Section } from "@/types";
import { SECTION_TITLE_H } from "@/constants";

const TITLE_GAP = 8;
const HANDLE = 10;
const MIN_W = 150;
const MIN_H = 80;

type ResizeDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const RESIZE_CURSORS: Record<ResizeDir, string> = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  ne: "nesw-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
  sw: "nesw-resize",
};

export function SectionCard({
  section,
  scaleRef,
  selected,
  onSelect,
  onDragStart,
  onMove,
  onSavePosition,
  onTitleChange,
  onResize,
  onSaveSize,
}: {
  section: Section;
  scaleRef: React.RefObject<number>;
  selected: boolean;
  onSelect: (id: string) => void;
  onDragStart?: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
  onSavePosition: (id: string, x: number, y: number) => void;
  onTitleChange: (id: string, title: string) => void;
  onResize: (id: string, x: number, y: number, w: number, h: number) => void;
  onSaveSize: (id: string, x: number, y: number, w: number, h: number) => void;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [lifted, setLifted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const didDrag = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const sectionRef = useRef(section);
  sectionRef.current = section;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onSaveRef = useRef(onSavePosition);
  onSaveRef.current = onSavePosition;
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;
  const onSaveSizeRef = useRef(onSaveSize);
  onSaveSizeRef.current = onSaveSize;

  const resizeState = useRef<{
    dir: ResizeDir;
    startX: number;
    startY: number;
    startSW: number;
    startSH: number;
    startSX: number;
    startSY: number;
  } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (editingTitle) return;
    if (resizeState.current) return;
    e.stopPropagation();
    onSelect(section._id);
    onDragStart?.(section._id);
    dragging.current = true;
    didDrag.current = false;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
    setLifted(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (resizeState.current) {
      const s = scaleRef.current ?? 1;
      const dx = (e.clientX - resizeState.current.startX) / s;
      const dy = (e.clientY - resizeState.current.startY) / s;
      const { dir, startSW, startSH, startSX, startSY } = resizeState.current;
      let x = startSX, y = startSY, w = startSW, h = startSH;
      if (dir.includes("e")) w = Math.max(MIN_W, startSW + dx);
      if (dir.includes("w")) { w = Math.max(MIN_W, startSW - dx); x = startSX + startSW - w; }
      if (dir.includes("s")) h = Math.max(MIN_H, startSH + dy);
      if (dir.includes("n")) { h = Math.max(MIN_H, startSH - dy); y = startSY + startSH - h; }
      onResizeRef.current(sectionRef.current._id, x, y, w, h);
      return;
    }
    if (!dragging.current) return;
    const s = scaleRef.current ?? 1;
    const dx = (e.clientX - lastMouse.current.x) / s;
    const dy = (e.clientY - lastMouse.current.y) / s;
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) didDrag.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    const sec = sectionRef.current;
    onMoveRef.current(sec._id, sec.x + dx, sec.y + dy);
  };

  const onPointerUp = () => {
    if (resizeState.current) {
      document.body.style.cursor = "";
      const sec = sectionRef.current;
      onSaveSizeRef.current(sec._id, sec.x, sec.y, sec.w, sec.h);
      resizeState.current = null;
      return;
    }
    if (!dragging.current) return;
    dragging.current = false;
    setLifted(false);
    if (didDrag.current) {
      const sec = sectionRef.current;
      onSaveRef.current(sec._id, sec.x, sec.y);
    }
  };

  const startResize = (e: React.PointerEvent<HTMLDivElement>, dir: ResizeDir) => {
    e.stopPropagation();
    const sec = sectionRef.current;
    resizeState.current = {
      dir,
      startX: e.clientX,
      startY: e.clientY,
      startSW: sec.w,
      startSH: sec.h,
      startSX: sec.x,
      startSY: sec.y,
    };
    document.body.style.cursor = RESIZE_CURSORS[dir];
    wrapperRef.current?.setPointerCapture(e.pointerId);
  };

  const bodyTop = SECTION_TITLE_H + TITLE_GAP;

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "absolute",
        left: section.x,
        top: section.y,
        width: section.w,
        height: bodyTop + section.h,
        pointerEvents: "auto",
        zIndex: 1,
        userSelect: "none",
        cursor: lifted ? "grabbing" : "grab",
        filter: lifted ? "drop-shadow(0 8px 24px rgba(0,0,0,0.18))" : "none",
        transition: lifted ? "filter 0.08s ease-out" : "filter 0.18s ease-out",
        overflow: "visible",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
    >
      {/* Floating title chip */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 2,
          height: SECTION_TITLE_H,
          display: "inline-flex",
          alignItems: "center",
          paddingLeft: 10,
          paddingRight: 10,
          maxWidth: section.w - 8,
          background: "var(--background)",
          border: selected ? "1.5px solid #0091ff" : "1.5px solid var(--border)",
          borderRadius: 8,
          boxSizing: "border-box",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        {editingTitle ? (
          <input
            autoFocus
            value={section.title}
            onChange={(e) => onTitleChange(section._id, e.target.value)}
            onBlur={() => setEditingTitle(false)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter" || e.key === "Escape") setEditingTitle(false);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--foreground)",
              width: Math.max(80, section.w - 40),
              padding: 0,
            }}
          />
        ) : (
          <span
            onClick={(e) => { e.stopPropagation(); setEditingTitle(true); }}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--foreground)",
              cursor: "text",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {section.title || "Untitled"}
          </span>
        )}
      </div>

      {/* Body */}
      <div
        style={{
          position: "absolute",
          top: bodyTop,
          left: 0,
          width: section.w,
          height: section.h,
        }}
      >
        {/* Frame */}
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            border: selected ? "2px solid #0091ff" : "1.5px solid var(--border)",
            borderRadius: 16,
            overflow: "hidden",
            background: "var(--muted)",
            boxSizing: "border-box",
          }}
        >
          {Array.from({ length: section.parts }, (_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                position: "relative",
                borderLeft: i > 0 ? "1px dashed var(--border)" : "none",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  bottom: 10,
                  right: 12,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--muted-foreground)",
                  opacity: 0.25,
                  lineHeight: 1,
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                {i + 1}
              </span>
            </div>
          ))}
        </div>

        {/* Resize handles — edges */}
        <div
          style={{ position: "absolute", top: HANDLE / 2, bottom: HANDLE / 2, right: -HANDLE / 2, width: HANDLE, cursor: "ew-resize", zIndex: 2 }}
          onPointerDown={(e) => startResize(e, "e")}
        />
        <div
          style={{ position: "absolute", top: HANDLE / 2, bottom: HANDLE / 2, left: -HANDLE / 2, width: HANDLE, cursor: "ew-resize", zIndex: 2 }}
          onPointerDown={(e) => startResize(e, "w")}
        />
        <div
          style={{ position: "absolute", left: HANDLE / 2, right: HANDLE / 2, bottom: -HANDLE / 2, height: HANDLE, cursor: "ns-resize", zIndex: 2 }}
          onPointerDown={(e) => startResize(e, "s")}
        />
        <div
          style={{ position: "absolute", left: HANDLE / 2, right: HANDLE / 2, top: -HANDLE / 2, height: HANDLE, cursor: "ns-resize", zIndex: 2 }}
          onPointerDown={(e) => startResize(e, "n")}
        />

        {/* Resize handles — corners */}
        <div
          style={{ position: "absolute", top: -HANDLE / 2, right: -HANDLE / 2, width: HANDLE, height: HANDLE, cursor: "nesw-resize", zIndex: 3 }}
          onPointerDown={(e) => startResize(e, "ne")}
        />
        <div
          style={{ position: "absolute", top: -HANDLE / 2, left: -HANDLE / 2, width: HANDLE, height: HANDLE, cursor: "nwse-resize", zIndex: 3 }}
          onPointerDown={(e) => startResize(e, "nw")}
        />
        <div
          style={{ position: "absolute", bottom: -HANDLE / 2, right: -HANDLE / 2, width: HANDLE, height: HANDLE, cursor: "nwse-resize", zIndex: 3 }}
          onPointerDown={(e) => startResize(e, "se")}
        />
        <div
          style={{ position: "absolute", bottom: -HANDLE / 2, left: -HANDLE / 2, width: HANDLE, height: HANDLE, cursor: "nesw-resize", zIndex: 3 }}
          onPointerDown={(e) => startResize(e, "sw")}
        />
      </div>
    </div>
  );
}
